"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import "../themes/brands/base.theme.js";
import Footer from "../components/common/Footer/Footer.js";
import Header from "../components/common/Header/Header.js";
import "../contexts/environment/EnvironmentContext.js";
import { EnvironmentProvider } from "../contexts/environment/EnvironmentProvider.js";
import "../contexts/language/LanguageContext.js";
import { LanguageProvider } from "../contexts/language/LanguageProvider.js";
import { t as translations } from "../translations-DcLD4XBj.js";
import "../contexts/polymorphic/PolymorphicContext.js";
import { PolymorphicProvider } from "../contexts/polymorphic/PolymorphicProvider.js";
import "../contexts/tracking/TrackingContext.js";
import { TrackingProvider } from "../contexts/tracking/TrackingProvider.js";
import Grid from "../components/layout/Grid/Grid.js";
import ScrollToTop from "../components/overlay/ScrollToTop/ScrollToTop.js";
import { themeResolver, toCssVariables } from "../themes/themeResolver.js";
import "../themes/brands/conferences.theme.js";
import "../themes/brands/ida.theme.js";
import "../themes/brands/insurance.theme.js";
import "../themes/brands/student.theme.js";
import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { useLocalStorage, useIsomorphicEffect } from "@mantine/hooks";
import { Notifications } from "@mantine/notifications";
import Helmet from "./Helmet.js";
import { HelmetProvider } from "react-helmet-async";
import { Children } from "react";
import '../assets/AppShell.css';const AppShell = ({
  children,
  clean,
  headerData,
  footerData,
  hideFooter,
  polymorphic,
  trackingManager,
  selfhostFonts = false,
  theme = "ida",
  scrollToTop = true,
  language,
  environmentVariables,
  ...rest
}) => {
  const child = Children.only(children);
  const withHeader = !clean && headerData;
  const withFooter = !clean && !hideFooter;
  const [lang, setLang] = useLocalStorage({
    key: "figurine-language",
    defaultValue: language || "da",
    getInitialValueInEffect: true
  });
  const [envVariables, setEnvVariable] = useLocalStorage({
    key: "figurine-environment",
    defaultValue: environmentVariables || { env: "ci" },
    getInitialValueInEffect: true
  });
  const setLanguage = (value) => {
    setLang(value);
  };
  const setEnvironmentVariable = ({ key, value }) => {
    setEnvVariable({ ...envVariables, [key]: value });
  };
  useIsomorphicEffect(() => {
    if (language) {
      setLanguage(language);
    } else {
      const urlLang = window.location.pathname.split("/").filter((x) => x)[0];
      const availableLanguages = Object.keys(translations);
      if (urlLang !== lang && availableLanguages.includes(urlLang)) {
        setLanguage(urlLang);
      }
    }
    if (environmentVariables) {
      Object.keys(environmentVariables).forEach((key) => {
        setEnvironmentVariable({ key, value: environmentVariables[key] });
      });
    }
  }, []);
  const rows = ["1fr"];
  if (!clean && headerData) {
    rows.unshift("max-content");
  }
  if (!clean && footerData) {
    rows.push("max-content");
  }
  const resolvedTheme = themeResolver(theme);
  const cssVariablesResolver = () => {
    const variables = toCssVariables(resolvedTheme.other || {});
    return {
      variables,
      light: variables,
      dark: variables
    };
  };
  return /* @__PURE__ */ jsx(
    MantineProvider,
    {
      classNamesPrefix: "figurine",
      cssVariablesResolver,
      theme: resolvedTheme,
      children: /* @__PURE__ */ jsx(HelmetProvider, { children: /* @__PURE__ */ jsx(TrackingProvider, { manager: trackingManager, children: /* @__PURE__ */ jsx(PolymorphicProvider, { components: polymorphic || {}, children: /* @__PURE__ */ jsx(LanguageProvider, { language: lang, setLanguage, children: /* @__PURE__ */ jsx(EnvironmentProvider, { variables: envVariables, setVariable: setEnvironmentVariable, children: /* @__PURE__ */ jsxs(DatesProvider, { settings: { locale: lang, firstDayOfWeek: 1, weekendDays: [0, 6] }, children: [
        /* @__PURE__ */ jsx(Helmet, { selfhostFonts }),
        /* @__PURE__ */ jsxs(
          Grid,
          {
            ...rest,
            container: true,
            rows: rows.join(" "),
            spacing: 0,
            style: { minHeight: clean ? void 0 : "100vh" },
            children: [
              withHeader && /* @__PURE__ */ jsx(Header, { ...headerData }),
              child,
              withFooter && /* @__PURE__ */ jsx(Footer, { ...footerData })
            ]
          }
        ),
        scrollToTop && /* @__PURE__ */ jsx(ScrollToTop, {}),
        /* @__PURE__ */ jsx(Notifications, {})
      ] }) }) }) }) }) })
    }
  );
};
export {
  AppShell as default
};
